import React from "react";
import { ChevronRight, Clock, Search } from "lucide-react";
import MobileSelect from "../../components/MobileSelectDropdown";
import AppInput from "../../components/ui/AppInput";

export default function CounselingMobileListBoard({
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  searchQuery,
  setSearchQuery,
  sortType,
  setSortType,
  filteredStudents,
  theme,
  visitLogs,
  thisMonthLogs,
  longAbsentStudents,
  selectedStudentId,
  handleStudentClick,
  getDeptColorClass,
  filterType,
  setFilterType,
}) {
  const accentColor = theme?.primaryHex || "#354F38";

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-[#FDFCFB]" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div className="flex-shrink-0 bg-white/80 backdrop-blur-md border-b border-gray-100 px-3 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <AppInput
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            placeholder="이름으로 찾기"
            icon={Search}
            className="h-11 bg-gray-50 border-none text-sm font-black text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-[#354F38]/10"
          />

          <div className="shrink-0 bg-gray-50 h-11 px-4 rounded-2xl flex items-center justify-center border border-gray-50">
            <span className="text-xs font-black text-gray-400">
              총 <span className="text-gray-900 text-sm ml-1">{filteredStudents.length}</span>명
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative shrink-0 flex-1">
            <MobileSelect
              value={sortType}
              options={[
                { value: "name", label: "이름순" },
                { value: "recent", label: "최근 심방순" },
                { value: "count", label: "심방 횟수순" },
              ]}
              onChange={setSortType}
            />
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 px-3 h-10 rounded-2xl border border-emerald-100 shadow-sm">
            <span className="text-sm font-black text-emerald-600">{thisMonthLogs.length}</span>
            <span className="text-[10px] font-black text-emerald-400 uppercase">이번 달</span>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {[
            { key: "all", label: "전체" },
            { key: "recent", label: "최근 심방" },
            { key: "long_absent", label: "장기 결석" }
          ].map((type) => (
            <button
              key={type.key}
              onClick={() => setFilterType(type.key)}
              className={`px-4 py-2 rounded-xl text-[12px] font-bold whitespace-nowrap transition-all duration-300 ${filterType === type.key ? "text-white shadow-md shadow-gray-200" : "bg-gray-50 text-gray-400 border border-gray-100"}`}
              style={filterType === type.key ? { backgroundColor: accentColor } : {}}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pb-32 pt-2 px-3">
        <div className="space-y-3">
          {filteredStudents.length > 0 ? (
            filteredStudents.map((student) => {
              const isSelected = student.id === selectedStudentId;
              const studentLogs = visitLogs.filter((l) => l.studentId === student.id);
              const lastLog = [...studentLogs].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
              const logCount = studentLogs.length;

              return (
                <div
                  key={student.id}
                  onClick={() => handleStudentClick(student.id)}
                  className={`p-4 rounded-[1.8rem] cursor-pointer transition-all border active:scale-95 ${isSelected ? "bg-white border-transparent shadow-lg ring-1 ring-gray-100" : "bg-white/60 border-transparent shadow-sm"}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white shadow-sm flex-shrink-0 border border-gray-50">
                      {student.photoUrl ? (
                        <img src={student.photoUrl} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-50">
                          <img
                            src={student.gender === "남" ? "/images/boy_3d_light.png" : "/images/girl_3d_light.png"}
                            className="w-full h-full object-cover scale-110"
                            alt="avatar"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-black text-base text-gray-800 tracking-tight">{student.name}</span>
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400">{student.grade}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-bold text-gray-400 flex items-center gap-1">
                          <Clock size={10} /> {lastLog ? lastLog.date.slice(5).replace('-', '.') : "기록 없음"}
                        </span>
                        {logCount > 0 && (
                          <span className="text-[11px] font-black text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100/50">
                            {logCount}회 심방
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight size={18} className="text-gray-200" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="py-20 flex flex-col items-center justify-center opacity-30">
              <Search size={48} className="text-gray-300 mb-3" />
              <p className="text-sm font-black text-gray-400 tracking-widest">결과가 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
